import { ApolloError } from '@apollo/client';
import * as React from 'react';
import { useHistory } from 'react-router';
import Cookies from 'universal-cookie';
import { AppContext } from '../AppContext';
import { Button } from './Button';
import { LogoutButton } from './LogoutButton';
import './Dialogs.css';

interface IDialogContainerProps {
  open: boolean;
  message: React.ReactElement<any, any>;
  title?: string;
}

/**
 * ダイアログが開いている間、Escキーが押されたらonEscapeを呼ぶ。
 * onEscapeは描画のたびに作り直されるので、最新の関数をrefに保持して、キー入力の監視は開閉時にだけ付け外しする。
 */
export function useEscapeKey(active: boolean, onEscape: () => void) {
  const onEscapeRef = React.useRef(onEscape);
  onEscapeRef.current = onEscape;

  React.useEffect(() => {
    if (!active) {
      return;
    }

    const handleKeyDown = function (event: KeyboardEvent) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        onEscapeRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active]);
}

export const DialogContainer: React.FC<IDialogContainerProps> = function (props) {
  return (
    <div className="Dialog-background">
      <div className="Dialog" role="dialog" aria-modal="true">
        <div className="Dialog-main">
          <h2 className="Dialog-title">{props.title}</h2>
          {props.message}
        </div>
        <div className="Dialog-button_container">{props.children}</div>
      </div>
    </div>
  );
};

interface INotifyDialogProps extends IDialogContainerProps {
  yesLabel?: string;
  onYes: () => void;
}

export const NotifyDialog: React.FC<INotifyDialogProps> = function (props) {
  useEscapeKey(props.open, () => props.onYes?.());

  if (!props.open) {
    return <div></div>;
  }

  return (
    <DialogContainer {...props}>
      <Button mini={true} onClick={() => props.onYes?.()}>{props.yesLabel ?? 'OK'}</Button>
    </DialogContainer>
  );
};

interface IConfirmDialogProps extends IDialogContainerProps {
  yesLabel?: string;
  noLabel?: string;
  onYes: () => void;
  onNo?: () => void;
  /** trueのとき、実行ボタンを危険な操作を表す赤で表示する */
  danger?: boolean;
}

function getConfirmButtonColor(danger: boolean | undefined): 'normal' | 'red' {
  if (danger) {
    return 'red';
  }
  return 'normal';
}

export const ConfirmDialog: React.FC<IConfirmDialogProps> = function (props) {
  useEscapeKey(props.open, () => props.onNo?.());

  if (!props.open) {
    return <div></div>;
  }

  const yesColor = getConfirmButtonColor(props.danger);

  return (
    <DialogContainer {...props}>
      <Button mini={true}
        color="white"
        onClick={() => props.onNo?.()}
      >
        {props.noLabel ?? 'キャンセル'}
      </Button>
      <Button mini={true} color={yesColor} onClick={() => props.onYes?.()}>
        {props.yesLabel ?? 'OK'}
      </Button>
    </DialogContainer>
  );
};

interface IFatalErrorDialogProps {}

/**
 * 画面を表示できないエラーのダイアログ。
 * ログインの有効期限が切れたときのように再読み込みしても直らない場合に画面から抜け出せるよう、［再読み込み］の横に［ログアウト］を置く
 */
export const FatalErrorDialog: React.FC<IFatalErrorDialogProps> = function (
  props
) {
  return (
    <DialogContainer
      {...props}
      title="エラー"
      message={<p>エラーが発生しました</p>}
      open={true}
    >
      <LogoutButton />
      <Button mini={true} onClick={() => window.location.reload()}>
        再読み込み
      </Button>
    </DialogContainer>
  );
};

interface IApiErrorDialogProps {
  error: Error;
}

export const ApiErrorDialog: React.FC<IApiErrorDialogProps> = function (props) {
  return (
    <DialogContainer
      {...props}
      title="通信エラー"
      message={<p>{props.error.message}</p>}
      open={true}
    >
      <Button mini={true}
        onClick={() => window.location.reload()}>
        再読み込み
      </Button>
    </DialogContainer>
  );
};

interface IGraphQLErrorDialogProps {
  error: ApolloError;
}

/** サーバーのエラーで画面を表示できないときのダイアログ。FatalErrorDialogと同じく、抜け出せるよう［ログアウト］を置く */
export const GraphQLErrorDialog: React.FC<IGraphQLErrorDialogProps> = function (
  props
) {
  return (
    <DialogContainer
      {...props}
      title="サーバーエラー"
      message={<p>{props.error.message}</p>}
      open={true}
    >
      <LogoutButton />
      <Button mini={true} onClick={() => window.location.reload()}>
        再読み込み
      </Button>
    </DialogContainer>
  );
};

interface ISvgNameInputDialogProps {
  open: boolean;
  svgItems: { fileName: string; svg: string }[];
  onConfirm: (items: { name: string; svg: string }[]) => void;
  onCancel: () => void;
  editMode?: boolean;
}

export const SvgNameInputDialog: React.FC<ISvgNameInputDialogProps> = function (
  props
) {
  // ファイル名から拡張子を除去してデフォルト名として使用
  const getDefaultName = (fileName: string) => {
    return fileName.replace(/\.[^/.]+$/, '');
  };

  // 編集モードの場合はfileNameをそのまま初期値として使用
  const getInitialNames = () => {
    return props.svgItems.map((item) => (props.editMode ? item.fileName : ''));
  };

  const [names, setNames] = React.useState<string[]>(getInitialNames);

  useEscapeKey(props.open && props.svgItems.length > 0, () => props.onCancel());

  // svgItemsが変更されたらnamesをリセット
  React.useEffect(() => {
    setNames(getInitialNames());
  }, [props.svgItems, props.editMode]);

  if (!props.open || props.svgItems.length === 0) {
    return null;
  }

  const handleNameChange = (index: number, value: string) => {
    const newNames = [...names];
    newNames[index] = value;
    setNames(newNames);
  };

  const handleConfirm = () => {
    const result = props.svgItems.map((item, index) => ({
      name: names[index] || getDefaultName(item.fileName),
      svg: item.svg,
    }));
    props.onConfirm(result);
  };

  return (
    <div className="Dialog-background">
      <div className="Dialog SvgNameInputDialog" role="dialog" aria-modal="true">
        <div className="Dialog-main">
          <h2 className="Dialog-title">{props.editMode ? 'シンボル名を編集' : 'シンボル名を入力'}</h2>
          <div className="SvgNameInputDialog-list">
            {props.svgItems.map((item, index) => (
              <div key={index} className="SvgNameInputDialog-row">
                <div className="SvgNameInputDialog-svg">
                  <img
                    src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(
                      item.svg
                    )
                      .replace(/'/g, '%27')
                      .replace(/"/g, '%22')}`}
                    alt={item.fileName}
                  />
                </div>
                <input
                  type="text"
                  className="SvgNameInputDialog-input"
                  placeholder={
                    props.editMode ? '' : getDefaultName(item.fileName)
                  }
                  value={names[index]}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="Dialog-button_container">
          <Button mini={true} color="white" onClick={props.onCancel}>キャンセル</Button>
          <Button mini={true} onClick={handleConfirm}>{props.editMode ? '保存' : '追加'}</Button>
        </div>
      </div>
    </div>
  );
};

interface ISvgDeleteConfirmDialogProps {
  open: boolean;
  svg: string;
  name?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const SvgDeleteConfirmDialog: React.FC<ISvgDeleteConfirmDialogProps> = function (props) {
  useEscapeKey(props.open, () => props.onCancel());

  if (!props.open) {
    return null;
  }

  return (
    <div className="Dialog-background">
      <div className="Dialog SvgDeleteConfirmDialog" role="dialog" aria-modal="true">
        <div className="Dialog-main">
          <h2 className="Dialog-title">削除の確認</h2>
          <p className="SvgDeleteConfirmDialog-message">このSVGを削除しますか？</p>
          <div className="SvgDeleteConfirmDialog-preview">
            <div className="SvgDeleteConfirmDialog-svg">
              <img
                src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(
                  props.svg
                )
                  .replace(/'/g, '%27')
                  .replace(/"/g, '%22')}`}
                alt="削除対象のSVG"
              />
            </div>
            {props.name && (
              <div className="SvgDeleteConfirmDialog-name">{props.name}</div>
            )}
          </div>
        </div>
        <div className="Dialog-button_container">
          <Button mini={true} color="white" onClick={props.onCancel}>キャンセル</Button>
          <Button mini={true} color="red" onClick={props.onConfirm}>削除</Button>
        </div>
      </div>
    </div>
  );
};
